import React, { useEffect, useState } from "react";
import { Mail, Building2, Inbox, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Eye } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "closed"];
const STATUS_CLS = {
  new: "bg-emerald-50 text-emerald-700 border-emerald-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success(`Lead marked ${status}`);
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const [digesting, setDigesting] = useState(false);
  const sendDigests = async () => {
    setDigesting(true);
    try {
      const { data } = await api.post("/admin/employer-digests/run?force=true");
      toast.success(data.sent.length ? `Monthly summary sent to: ${data.sent.join(", ")}` : `No digests sent (${data.eligible} claimed compan${data.eligible === 1 ? "y" : "ies"}; check Resend/contact email)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setDigesting(false); }
  };
  const [claimed, setClaimed] = useState([]);
  const [previewCo, setPreviewCo] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => { api.get("/employer/companies").then((r) => { setClaimed(r.data); if (r.data[0]) setPreviewCo(r.data[0].name); }).catch(() => {}); }, []);
  const openPreview = async () => {
    try { const { data } = await api.get(`/admin/employer-digests/preview?company=${encodeURIComponent(previewCo)}`); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not build preview"); }
  };
  const digestBar = (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="employer-digest-bar">
      <p className="text-sm text-slate-600"><b className="text-slate-900">Monthly employer summary</b> — emailed automatically to every claimed company every 30 days (new reviews, Speak-Up score, awaiting responses, trajectory).</p>
      <div className="flex flex-wrap items-center gap-2">
        {claimed.length > 0 && (
          <>
            <select data-testid="digest-company-select" value={previewCo} onChange={(e) => setPreviewCo(e.target.value)} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs">
              {claimed.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <Button data-testid="preview-digest-btn" size="sm" variant="outline" onClick={openPreview} className="rounded-full gap-1.5"><Eye size={14} /> Preview</Button>
          </>
        )}
        <Button data-testid="send-digests-btn" size="sm" variant="outline" disabled={digesting} onClick={sendDigests} className="rounded-full gap-1.5"><Send size={14} /> {digesting ? "Sending..." : "Send digests now"}</Button>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl" style={{ fontWeight: 800 }}>Digest preview</DialogTitle>
            <DialogDescription>To: {preview?.to || "(no contact email)"} · Subject: {preview?.subject}{preview?.last_digest_at ? ` · last sent ${new Date(preview.last_digest_at).toLocaleDateString()}` : " · never sent"}</DialogDescription>
          </DialogHeader>
          {preview && <iframe title="digest" data-testid="digest-preview-frame" srcDoc={preview.html} className="w-full h-[60vh] bg-white border border-slate-200 rounded-lg" sandbox="" />}
        </DialogContent>
      </Dialog>
    </div>
  );

  if (!leads) return <p className="text-slate-400 font-mono text-sm">Loading...</p>;
  if (leads.length === 0) {
    return (
      <>{digestBar}
      <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center" data-testid="leads-empty">
        <Inbox className="mx-auto text-slate-300 mb-3" size={40} />
        <p className="text-slate-500">No demo requests yet. They'll appear here and land in your inbox.</p>
      </div></>
    );
  }

  return (
    <>{digestBar}
    <div className="space-y-4" data-testid="leads-list">
      {leads.map((l) => (
        <div key={l.id} data-testid={`lead-card-${l.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-xl text-slate-900" style={{ fontWeight: 700 }}>{l.name}</h3>
                <span data-testid={`lead-status-${l.id}`} className={`text-[11px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_CLS[l.status] || STATUS_CLS.new}`}>{l.status}</span>
                {!l.emailed && <span className="text-[11px] font-mono text-rose-500">email not sent</span>}
              </div>
              <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1.5"><Building2 size={13} /> {l.company}{l.role ? ` · ${l.role}` : ""}{l.company_size ? ` · ${l.company_size}` : ""}</span>
                <a href={`mailto:${l.work_email}`} className="flex items-center gap-1.5 text-emerald-700 hover:underline"><Mail size={13} /> {l.work_email}</a>
              </div>
            </div>
            <span className="text-xs text-slate-400 font-mono">{new Date(l.created_at).toLocaleDateString()}</span>
          </div>
          {l.message && <p className="text-slate-700 mt-3 text-sm leading-relaxed border-l-2 border-slate-200 pl-3 whitespace-pre-wrap">{l.message}</p>}
          <div className="flex gap-2 mt-4">
            {STATUSES.filter((s) => s !== l.status).map((s) => (
              <button key={s} data-testid={`lead-set-${s}-${l.id}`} onClick={() => setStatus(l.id, s)} className="text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors">
                Mark {s}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div></>
  );
}
